import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { moderationApi, MODERATION_CARD_TYPES, MODERATION_ACTION_STATUSES, CONTENT_ENTITY_TYPES } from '../../api/moderation';
import { platformRulesApi } from '../../api/platformRules';
import { usersApi } from '../../api/users';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import { isAdmin } from '../utils/roles';
import { UserContext } from '../../context/UserContext';
import { useContext } from 'react';
import './AdminModeration.css';

const CARD_TYPE_OPTIONS = Object.entries(MODERATION_CARD_TYPES).map(([value, info]) => ({
    value,
    label: info.label,
}));

const CONTENT_ENTITY_LABELS = {
    POST: 'Пост на форуме',
    COMMENT: 'Комментарий',
    MESSAGE: 'Сообщение в чате',
    PROFILE: 'Профиль пользователя',
    DUEL_WORK: 'Работа в баттле',
};

const STATUS_OPTIONS = Object.entries(MODERATION_ACTION_STATUSES).map(([value, label]) => ({
    value,
    label,
}));

const getCardBadgeVariant = (cardType) => {
    switch (cardType) {
        case 'PINK_CARD': return 'info';
        case 'YELLOW_CARD': return 'warning';
        case 'RED_CARD': return 'danger';
        case 'BLACKLIST': return 'dark';
        case 'CONTENT_REMOVAL': return 'secondary';
        default: return 'secondary';
    }
};

const getStatusBadgeVariant = (status) => {
    switch (status) {
        case 'ACTIVE': return 'success';
        case 'EXPIRED': return 'secondary';
        case 'REVOKED': return 'info';
        default: return 'secondary';
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getRowColorClass = (cardType) => {
    switch (cardType) {
        case 'PINK_CARD': return 'admin-moderation__row--pink';
        case 'YELLOW_CARD': return 'admin-moderation__row--yellow';
        case 'RED_CARD': return 'admin-moderation__row--red';
        case 'BLACKLIST': return 'admin-moderation__row--black';
        default: return '';
    }
};

const escapeCsv = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const downloadCsv = (rows, filename) => {
    const csv = rows.map(r => r.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const EMPTY_CARD_FORM = {
    targetUserId: '',
    cardType: 'PINK_CARD',
    reason: '',
    platformRuleId: '',
    durationDays: '',
    ipAddress: '',
    hardwareId: '',
    violations: '',
    contentEntityType: '',
    contentEntityId: '',
};

const AdminModeration = () => {
    const { currentUser } = useContext(UserContext);
    const admin = isAdmin(currentUser);

    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    // Filters
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCardType, setFilterCardType] = useState('');

    // Apply card modal
    const [showCardModal, setShowCardModal] = useState(false);
    const [cardForm, setCardForm] = useState({ ...EMPTY_CARD_FORM });
    const [platformRules, setPlatformRules] = useState([]);

    // Revoke modal
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revokeReason, setRevokeReason] = useState('');

    // IP check modal
    const [showIpModal, setShowIpModal] = useState(false);
    const [ipInput, setIpInput] = useState('');
    const [ipResult, setIpResult] = useState(null);

    // HWID check modal
    const [showHwidModal, setShowHwidModal] = useState(false);
    const [hwidInput, setHwidInput] = useState('');
    const [hwidResult, setHwidResult] = useState(null);

    // My actions modal
    const [showMyActionsModal, setShowMyActionsModal] = useState(false);
    const [myActions, setMyActions] = useState([]);
    const [myActionsPage, setMyActionsPage] = useState(0);
    const [myActionsTotalPages, setMyActionsTotalPages] = useState(0);
    const [myActionsTotal, setMyActionsTotal] = useState(0);
    const [myActionsLoading, setMyActionsLoading] = useState(false);

    // Organizer sanction modal
    const [showOrganizerModal, setShowOrganizerModal] = useState(false);
    const [organizerUserId, setOrganizerUserId] = useState('');
    const [organizerReason, setOrganizerReason] = useState('');
    const [organizerResult, setOrganizerResult] = useState(null);

    // User search
    const [userSearch, setUserSearch] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // User history modal
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyUser, setHistoryUser] = useState(null);
    const [historyActions, setHistoryActions] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    // Detail modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailAction, setDetailAction] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailCanAppeal, setDetailCanAppeal] = useState(null);

    // Mute check
    const [muteChecks, setMuteChecks] = useState({});

    // Appeal badges (actionId -> boolean)
    const [appealFlags, setAppealFlags] = useState({});

    // Stats
    const [stats, setStats] = useState(null);

    const loadActions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage - 1,
                size: pageSize,
                sortBy: 'createdAt',
                direction: 'DESC',
                ...(filterStatus && { status: filterStatus }),
                ...(filterCardType && { cardType: filterCardType }),
            };
            const data = await moderationApi.listActions(params);
            const list = data?.content || [];
            const total = data?.totalElements || list.length;
            setActions(list);
            setTotalItems(total);
        } catch (err) {
            setError('Не удалось загрузить модераторские действия');
            console.error('[AdminModeration] Failed to load:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterStatus, filterCardType]);

    // Load stats
    const loadStats = useCallback(async () => {
        try {
            const data = await moderationApi.listActions({ size: 1, page: 0 });
            const total = data?.totalElements || 0;
            const [active, expired, revoked] = await Promise.all([
                moderationApi.listActions({ size: 1, status: 'ACTIVE' }).catch(() => null),
                moderationApi.listActions({ size: 1, status: 'EXPIRED' }).catch(() => null),
                moderationApi.listActions({ size: 1, status: 'REVOKED' }).catch(() => null),
            ]);
            setStats({
                total,
                active: active?.totalElements || 0,
                expired: expired?.totalElements || 0,
                revoked: revoked?.totalElements || 0,
            });
        } catch (err) {
            console.error('[AdminModeration] Stats load failed:', err);
        }
    }, []);

    useEffect(() => {
        loadActions();
    }, [loadActions]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    // Check appeal eligibility for active actions
    useEffect(() => {
        const activeActionIds = actions
            .filter(a => a.status === 'ACTIVE')
            .map(a => a.id);

        if (activeActionIds.length === 0) return;

        activeActionIds.forEach(async (id) => {
            try {
                const result = await moderationApi.canAppeal(id);
                setAppealFlags(prev => ({ ...prev, [id]: result.canAppeal }));
            } catch {
                // silently skip
            }
        });
    }, [actions]);

    // Load platform rules for the card form dropdown
    useEffect(() => {
        const loadRules = async () => {
            try {
                const data = await platformRulesApi.list({ size: 100, sortBy: 'sortOrder', direction: 'ASC' });
                setPlatformRules(data?.content || []);
            } catch (err) {
                console.error('[AdminModeration] Failed to load platform rules:', err);
            }
        };
        loadRules();
    }, []);

    // User search
    const handleUserSearch = async () => {
        if (!userSearch.trim()) return;
        try {
            setSearching(true);
            const data = await usersApi.listAll({ size: 10, username: userSearch.trim() });
            const list = Array.isArray(data) ? data : (data?.content || []);
            setUserSearchResults(list);
        } catch (err) {
            console.error('[AdminModeration] User search failed:', err);
            setUserSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const handleOpenCardModal = () => {
        setCardForm({ ...EMPTY_CARD_FORM });
        setUserSearch('');
        setUserSearchResults([]);
        setShowCardModal(true);
        setError(null);
    };

    const handleCloseCardModal = () => {
        setShowCardModal(false);
    };

    const handleApplyCard = async () => {
        if (!cardForm.targetUserId) {
            setError('Выберите пользователя');
            return;
        }
        if (!cardForm.reason.trim()) {
            setError('Укажите причину');
            return;
        }

        const cardInfo = MODERATION_CARD_TYPES[cardForm.cardType];
        const payload = {
            targetUserId: cardForm.targetUserId,
            cardType: cardForm.cardType,
            reason: cardForm.reason.trim(),
        };

        if (cardForm.platformRuleId) {
            payload.platformRuleId = cardForm.platformRuleId;
        }

        // Duration validation based on card type
        if (cardForm.cardType === 'YELLOW_CARD' || cardForm.cardType === 'RED_CARD') {
            const days = Number(cardForm.durationDays);
            if (!days || days < cardInfo.minDays || days > cardInfo.maxDays) {
                setError(`Длительность должна быть от ${cardInfo.minDays} до ${cardInfo.maxDays} дней`);
                return;
            }
            payload.durationDays = days;
        } else {
            payload.durationDays = null;
        }

        if (cardForm.cardType === 'BLACKLIST') {
            if (!cardForm.ipAddress.trim()) {
                setError('IP-адрес обязателен для BLACKLIST');
                return;
            }
            payload.ipAddress = cardForm.ipAddress.trim();
            if (cardForm.hardwareId.trim()) payload.hardwareId = cardForm.hardwareId.trim();
        }

        if (cardForm.cardType === 'CONTENT_REMOVAL') {
            if (!cardForm.contentEntityType) {
                setError('Укажите тип контента для удаления');
                return;
            }
            if (!cardForm.contentEntityId.trim()) {
                setError('Укажите ID контента для удаления');
                return;
            }
            payload.contentEntityType = cardForm.contentEntityType;
            payload.contentEntityId = cardForm.contentEntityId.trim();
        }

        if (cardForm.violations.trim()) {
            payload.violations = cardForm.violations.trim();
        }

        try {
            setActionLoading(true);
            await moderationApi.applyCard(payload);
            await loadActions();
            await loadStats();
            handleCloseCardModal();
        } catch (err) {
            setError(`Не удалось применить карточку: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenRevokeModal = (action) => {
        setRevokeTarget(action);
        setRevokeReason('');
        setShowRevokeModal(true);
        setError(null);
    };

    const handleRevoke = async () => {
        if (!revokeReason.trim()) {
            setError('Укажите причину отзыва');
            return;
        }
        try {
            setActionLoading(true);
            await moderationApi.revoke(revokeTarget.id, { reason: revokeReason.trim() });
            await loadActions();
            await loadStats();
            setShowRevokeModal(false);
            setRevokeTarget(null);
        } catch (err) {
            setError(`Не удалось отозвать: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDoubleDuration = async (action) => {
        if (!window.confirm(`Удвоить срок для действия #${action.id.slice(0, 8)}?`)) return;
        try {
            setActionLoading(true);
            await moderationApi.doubleDuration(action.id);
            await loadActions();
            await loadStats();
        } catch (err) {
            setError(`Не удалось удвоить срок: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckIp = async () => {
        if (!ipInput.trim()) return;
        try {
            setActionLoading(true);
            const result = await moderationApi.checkIpBlock(ipInput.trim());
            setIpResult(result);
        } catch (err) {
            setError(`Ошибка проверки IP: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckHwid = async () => {
        if (!hwidInput.trim()) return;
        try {
            setActionLoading(true);
            const result = await moderationApi.checkHwidBlock(hwidInput.trim());
            setHwidResult(result);
        } catch (err) {
            setError(`Ошибка проверки HWID: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const loadMyActions = useCallback(async (page = 0) => {
        try {
            setMyActionsLoading(true);
            const data = await moderationApi.getMyActions({ page, size: 10, sortBy: 'createdAt', direction: 'DESC' });
            setMyActions(data.content || []);
            setMyActionsPage(data.number ?? page);
            setMyActionsTotalPages(data.totalPages ?? 0);
            setMyActionsTotal(data.totalElements ?? 0);
        } catch (err) {
            setError(`Ошибка загрузки моих действий: ${err.response?.data?.message || err.message}`);
        } finally {
            setMyActionsLoading(false);
        }
    }, [setError]);

    const handleShowMyActions = () => {
        setShowMyActionsModal(true);
        loadMyActions(0);
    };

    const handleSanctionOrganizer = async () => {
        if (!organizerUserId.trim()) {
            setError('Укажите ID пользователя');
            return;
        }
        if (!organizerReason.trim()) {
            setError('Укажите причину');
            return;
        }
        try {
            setActionLoading(true);
            const result = await moderationApi.sanctionOrganizer(organizerUserId.trim(), { reason: organizerReason.trim() });
            setOrganizerResult(result);
            await loadActions();
            await loadStats();
        } catch (err) {
            setError(`Не удалось применить санкцию: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const currentCardInfo = MODERATION_CARD_TYPES[cardForm.cardType];

    const getPlatformRuleTitle = (ruleId) => {
        if (!ruleId) return null;
        const rule = platformRules.find(r => r.id === ruleId);
        return rule?.title || ruleId.slice(0, 8);
    };

    const handleOpenDetail = async (action) => {
        setDetailAction(action);
        setShowDetailModal(true);
        setDetailLoading(true);
        setDetailCanAppeal(null);
        try {
            const [fullAction, appealResult] = await Promise.all([
                moderationApi.getAction(action.id).catch(() => action),
                moderationApi.canAppeal(action.id).catch(() => null),
            ]);
            setDetailAction(fullAction);
            setDetailCanAppeal(appealResult?.canAppeal ?? null);
        } catch (err) {
            console.error('[AdminModeration] Detail load failed:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setDetailAction(null);
        setDetailCanAppeal(null);
    };

    const handleCheckMute = async (userId) => {
        try {
            setActionLoading(true);
            const result = await moderationApi.checkMuted(userId);
            setMuteChecks(prev => ({ ...prev, [userId]: result.muted }));
        } catch (err) {
            setError(`Не удалось проверить мут: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleExportCsv = () => {
        const headers = [
            'ID', 'Карточка', 'Нарушитель', 'Нарушитель ID',
            'Модератор', 'Модератор ID', 'Причина', 'Статус',
            'Длительность (дней)', 'Истекает', 'Создано',
            'IP', 'HWID', 'Нарушения', 'Удвоен срок',
            'Отозвано', 'Отозвал', 'Причина отзыва',
        ];
        const rows = actions.map(a => [
            a.id,
            a.cardTypeDisplayName || MODERATION_CARD_TYPES[a.cardType]?.label || a.cardType,
            a.targetUsername,
            a.targetUserId,
            a.moderatorUsername,
            a.moderatorId,
            a.reason,
            MODERATION_ACTION_STATUSES[a.status] || a.status,
            a.durationDays || '',
            a.expiresAt || '',
            a.createdAt || '',
            a.ipAddress || '',
            a.hardwareId || '',
            a.violations || '',
            a.durationDoubled ? 'да' : 'нет',
            a.revokedAt || '',
            a.revokedBy || '',
            a.revokeReason || '',
        ]);
        downloadCsv([headers, ...rows], `moderation_${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const handleOpenHistory = async (action) => {
        setHistoryUser({ id: action.targetUserId, username: action.targetUsername });
        setShowHistoryModal(true);
        setHistoryLoading(true);
        setHistoryError(null);
        setHistoryActions([]);
        try {
            const data = await moderationApi.getUserActions(action.targetUserId, {
                size: 50,
                sortBy: 'createdAt',
                direction: 'DESC',
            });
            setHistoryActions(data?.content || []);
        } catch (err) {
            setHistoryError('Не удалось загрузить историю модерации');
            console.error('[AdminModeration] History load failed:', err);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleCloseHistoryModal = () => {
        setShowHistoryModal(false);
        setHistoryUser(null);
        setHistoryActions([]);
        setHistoryError(null);
    };

    if (loading && actions.length === 0) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Модерация платформы</h1>

            {error && (
                <HrnbAlert type="danger">
                    {error}
                    <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => { setError(null); loadActions(); }}>
                        Повторить
                    </Button>
                </HrnbAlert>
            )}

            {/* Stats */}
            {stats && (
                <div className="admin-moderation__stats">
                    <div className="admin-moderation__stat-card">
                        <span className="admin-moderation__stat-value">{stats.total}</span>
                        <span className="admin-moderation__stat-label">Всего</span>
                    </div>
                    <div className="admin-moderation__stat-card admin-moderation__stat-card--active">
                        <span className="admin-moderation__stat-value">{stats.active}</span>
                        <span className="admin-moderation__stat-label">Активных</span>
                    </div>
                    <div className="admin-moderation__stat-card admin-moderation__stat-card--expired">
                        <span className="admin-moderation__stat-value">{stats.expired}</span>
                        <span className="admin-moderation__stat-label">Истёкших</span>
                    </div>
                    <div className="admin-moderation__stat-card admin-moderation__stat-card--revoked">
                        <span className="admin-moderation__stat-value">{stats.revoked}</span>
                        <span className="admin-moderation__stat-label">Отозванных</span>
                    </div>
                </div>
            )}

            {/* Toolbar */}
            <div className="admin-moderation__toolbar">
                <Form.Select
                    className="admin-moderation__filter"
                    value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                >
                    <option value="">Все статусы</option>
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </Form.Select>
                <Form.Select
                    className="admin-moderation__filter"
                    value={filterCardType}
                    onChange={(e) => { setFilterCardType(e.target.value); setCurrentPage(1); }}
                >
                    <option value="">Все карточки</option>
                    {CARD_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </Form.Select>
                <Button variant="primary" onClick={handleOpenCardModal}>
                    + Выдать карточку
                </Button>
                <Button variant="outline-success" onClick={handleExportCsv} disabled={actions.length === 0}>
                    Экспорт CSV
                </Button>
                <Button variant="outline-primary" onClick={handleShowMyActions}>
                    Мои действия
                </Button>
                {admin && (
                    <>
                        <Button variant="outline-info" onClick={() => { setShowIpModal(true); setIpResult(null); setIpInput(''); }}>
                            Проверить IP
                        </Button>
                        <Button variant="outline-info" onClick={() => { setShowHwidModal(true); setHwidResult(null); setHwidInput(''); }}>
                            Проверить HWID
                        </Button>
                        <Button variant="outline-danger" onClick={() => { setShowOrganizerModal(true); setOrganizerResult(null); setOrganizerUserId(''); setOrganizerReason(''); }}>
                            Санкция организатору
                        </Button>
                    </>
                )}
            </div>

            <HrnbCard>
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={10}>10 на странице</option>
                        <option value={20}>20 на странице</option>
                        <option value={50}>50 на странице</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-moderation__table">
                    <thead>
                        <tr>
                            <th>Карточка</th>
                            <th>Нарушитель</th>
                            <th>Модератор</th>
                            <th>Причина</th>
                            <th>Правило</th>
                            <th>Статус</th>
                            <th>Срок</th>
                            <th>Истекает</th>
                            <th>Дата</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {actions.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center text-muted">
                                    Действий не найдено
                                </td>
                            </tr>
                        ) : (
                            actions.map((action) => (
                                <tr key={action.id} className={getRowColorClass(action.cardType)}>
                                    <td>
                                        <Badge
                                            bg={getCardBadgeVariant(action.cardType)}
                                            className="admin-moderation__card-badge"
                                            onClick={() => handleOpenDetail(action)}
                                        >
                                            {action.cardTypeDisplayName || MODERATION_CARD_TYPES[action.cardType]?.label || action.cardType}
                                        </Badge>
                                        {action.durationDoubled && (
                                            <Badge bg="danger" className="ms-1">x2</Badge>
                                        )}
                                    </td>
                                    <td>
                                        <div className="admin-moderation__user">
                                            <strong
                                                className="admin-moderation__user-link"
                                                onClick={() => handleOpenHistory(action)}
                                                title="История модерации пользователя"
                                            >
                                                {action.targetUsername}
                                            </strong>
                                            <small className="text-muted">{action.targetUserId?.slice(0, 8)}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="admin-moderation__user">
                                            <strong>{action.moderatorUsername}</strong>
                                            <small className="text-muted">{action.moderatorId?.slice(0, 8)}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div
                                            className="admin-moderation__reason admin-moderation__reason-link"
                                            onClick={() => handleOpenDetail(action)}
                                            title="Подробности"
                                        >
                                            {action.reason?.substring(0, 80)}
                                            {action.reason?.length > 80 ? '...' : ''}
                                        </div>
                                        {action.violations && (
                                            <small className="text-muted">Нарушения: {action.violations}</small>
                                        )}
                                    </td>
                                    <td>
                                        {action.platformRuleId ? (
                                            <small className="admin-moderation__rule-ref">
                                                {getPlatformRuleTitle(action.platformRuleId)}
                                            </small>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        <Badge bg={getStatusBadgeVariant(action.status)}>
                                            {MODERATION_ACTION_STATUSES[action.status] || action.status}
                                        </Badge>
                                        {action.status === 'ACTIVE' && appealFlags[action.id] && (
                                            <Badge bg="info" className="ms-1" title="Можно подать апелляцию">
                                                Апелляция
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        {action.durationDays ? `${action.durationDays} дн.` : '—'}
                                    </td>
                                    <td>{formatDate(action.expiresAt)}</td>
                                    <td>{formatDate(action.createdAt)}</td>
                                    <td>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-1 mb-1"
                                            onClick={() => handleOpenDetail(action)}
                                        >
                                            Детали
                                        </Button>
                                        {action.status === 'ACTIVE' && (
                                            <>
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="me-1 mb-1"
                                                    onClick={() => handleOpenRevokeModal(action)}
                                                    disabled={actionLoading}
                                                >
                                                    Отозвать
                                                </Button>
                                                {admin && (action.cardType === 'YELLOW_CARD' || action.cardType === 'RED_CARD') && !action.durationDoubled && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="mb-1"
                                                        onClick={() => handleDoubleDuration(action)}
                                                        disabled={actionLoading}
                                                    >
                                                        x2 срок
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Apply card modal */}
            <Modal show={showCardModal} onHide={handleCloseCardModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Выдать карточку</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {/* User search */}
                        <Form.Group className="mb-3">
                            <Form.Label>Поиск пользователя</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    placeholder="Введите username..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUserSearch())}
                                />
                                <Button variant="outline-primary" onClick={handleUserSearch} disabled={searching}>
                                    {searching ? 'Поиск...' : 'Найти'}
                                </Button>
                            </div>
                            {userSearchResults.length > 0 && (
                                <div className="admin-moderation__search-results mt-2">
                                    {userSearchResults.map(u => (
                                        <div
                                            key={u.id}
                                            className={`admin-moderation__search-item${cardForm.targetUserId === u.id ? ' selected' : ''}`}
                                        >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="admin-moderation__search-item-info" onClick={() => { setCardForm({ ...cardForm, targetUserId: u.id }); }}>
                                                    <strong>{u.username}</strong>
                                                    <span className="text-muted ms-2">{u.email}</span>
                                                    <Badge bg="secondary" className="ms-2">{u.role}</Badge>
                                                </div>
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    onClick={() => handleCheckMute(u.id)}
                                                    disabled={actionLoading}
                                                >
                                                    {muteChecks[u.id] === undefined ? 'Мут?' : muteChecks[u.id] ? 'Замучен' : 'Не в муте'}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {cardForm.targetUserId && (
                                <Form.Text className="text-success">
                                    Выбран: {cardForm.targetUserId.slice(0, 8)}...
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Тип карточки</Form.Label>
                            <Form.Select
                                value={cardForm.cardType}
                                onChange={(e) => setCardForm({ ...cardForm, cardType: e.target.value, durationDays: '' })}
                            >
                                {CARD_TYPE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                            {currentCardInfo && (
                                <Form.Text className="text-muted">
                                    {currentCardInfo.description}
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Причина</Form.Label>
                            <Form.Control
                                type="text"
                                value={cardForm.reason}
                                onChange={(e) => setCardForm({ ...cardForm, reason: e.target.value })}
                                placeholder="Обоснование санкции"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Нарушенное правило платформы (опционально)</Form.Label>
                            <Form.Select
                                value={cardForm.platformRuleId}
                                onChange={(e) => setCardForm({ ...cardForm, platformRuleId: e.target.value })}
                            >
                                <option value="">— не выбрано —</option>
                                {platformRules.map(rule => (
                                    <option key={rule.id} value={rule.id}>
                                        {rule.title}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {(cardForm.cardType === 'YELLOW_CARD' || cardForm.cardType === 'RED_CARD') && currentCardInfo && (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Длительность (дней): {currentCardInfo.minDays}–{currentCardInfo.maxDays}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    min={currentCardInfo.minDays}
                                    max={currentCardInfo.maxDays}
                                    value={cardForm.durationDays}
                                    onChange={(e) => setCardForm({ ...cardForm, durationDays: e.target.value })}
                                />
                            </Form.Group>
                        )}

                        {cardForm.cardType === 'BLACKLIST' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>IP-адрес (обязательно)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={cardForm.ipAddress}
                                        onChange={(e) => setCardForm({ ...cardForm, ipAddress: e.target.value })}
                                        placeholder="192.168.1.1"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Hardware ID (опционально)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={cardForm.hardwareId}
                                        onChange={(e) => setCardForm({ ...cardForm, hardwareId: e.target.value })}
                                        placeholder="HWID-xxx"
                                    />
                                </Form.Group>
                            </>
                        )}

                        {cardForm.cardType === 'CONTENT_REMOVAL' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Тип контента</Form.Label>
                                    <Form.Select
                                        value={cardForm.contentEntityType}
                                        onChange={(e) => setCardForm({ ...cardForm, contentEntityType: e.target.value })}
                                    >
                                        <option value="">— выберите тип —</option>
                                        {Object.entries(CONTENT_ENTITY_TYPES).map(([key, val]) => (
                                            <option key={key} value={val}>
                                                {CONTENT_ENTITY_LABELS[val] || val}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>ID контента</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={cardForm.contentEntityId}
                                        onChange={(e) => setCardForm({ ...cardForm, contentEntityId: e.target.value })}
                                        placeholder="uuid-сущности"
                                    />
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Конкретные нарушения (опционально)</Form.Label>
                            <Form.Control
                                type="text"
                                value={cardForm.violations}
                                onChange={(e) => setCardForm({ ...cardForm, violations: e.target.value })}
                                placeholder="доксинг, угрозы и т.д."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCardModal}>Отмена</Button>
                    <Button variant="danger" onClick={handleApplyCard} disabled={actionLoading}>
                        {actionLoading ? 'Применение...' : 'Применить карточку'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Revoke modal */}
            <Modal show={showRevokeModal} onHide={() => setShowRevokeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Отзыв санкции</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Отзыв действия для: <strong>{revokeTarget?.targetUsername}</strong>
                    </p>
                    <Form.Group>
                        <Form.Label>Причина отзыва</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={revokeReason}
                            onChange={(e) => setRevokeReason(e.target.value)}
                            placeholder="Апелляция удовлетворена"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRevokeModal(false)}>Отмена</Button>
                    <Button variant="info" onClick={handleRevoke} disabled={actionLoading}>
                        {actionLoading ? 'Отзыв...' : 'Отозвать'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* IP check modal (ADMIN only) */}
            <Modal show={showIpModal} onHide={() => setShowIpModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Проверка блокировки IP</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>IP-адрес</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                value={ipInput}
                                onChange={(e) => setIpInput(e.target.value)}
                                placeholder="192.168.1.1"
                            />
                            <Button variant="outline-primary" onClick={handleCheckIp} disabled={actionLoading}>
                                Проверить
                            </Button>
                        </div>
                    </Form.Group>
                    {ipResult !== null && (
                        <div className="mt-3">
                            <Badge bg={ipResult.blocked ? 'danger' : 'success'}>
                                {ipResult.blocked ? 'Заблокирован' : 'Не заблокирован'}
                            </Badge>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowIpModal(false)}>Закрыть</Button>
                </Modal.Footer>
            </Modal>

            {/* HWID check modal (ADMIN only) */}
            <Modal show={showHwidModal} onHide={() => setShowHwidModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Проверка блокировки HWID</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Hardware ID</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                value={hwidInput}
                                onChange={(e) => setHwidInput(e.target.value)}
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            />
                            <Button variant="outline-primary" onClick={handleCheckHwid} disabled={actionLoading}>
                                Проверить
                            </Button>
                        </div>
                    </Form.Group>
                    {hwidResult !== null && (
                        <div className="mt-3">
                            <Badge bg={hwidResult.blocked ? 'danger' : 'success'}>
                                {hwidResult.blocked ? 'Заблокирован' : 'Не заблокирован'}
                            </Badge>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHwidModal(false)}>Закрыть</Button>
                </Modal.Footer>
            </Modal>

            {/* My actions modal (MODERATOR, ADMIN) */}
            <Modal show={showMyActionsModal} onHide={() => setShowMyActionsModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Мои модераторские действия ({myActionsTotal})</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {myActionsLoading ? (
                        <div className="text-center py-4"><HrnbLoader /></div>
                    ) : myActions.length === 0 ? (
                        <div className="text-center text-muted py-4">У вас пока нет модераторских действий</div>
                    ) : (
                        <>
                            <Table responsive hover size="sm" className="admin-moderation__table">
                                <thead>
                                    <tr>
                                        <th>Карточка</th>
                                        <th>Нарушитель</th>
                                        <th>Причина</th>
                                        <th>Дата</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {myActions.map(a => (
                                        <tr key={a.id} className={getRowColorClass(a.cardType)}>
                                            <td>
                                                <Badge bg={getCardBadgeVariant(a.cardType)}>
                                                    {a.cardTypeDisplayName || MODERATION_CARD_TYPES[a.cardType]?.label || a.cardType}
                                                </Badge>
                                            </td>
                                            <td>{a.targetUsername || a.targetUserId}</td>
                                            <td className="text-truncate" style={{ maxWidth: 250 }}>{a.reason}</td>
                                            <td>{formatDate(a.createdAt)}</td>
                                            <td>
                                                <Badge bg={getStatusBadgeVariant(a.status)}>
                                                    {MODERATION_ACTION_STATUSES[a.status] || a.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {myActionsTotalPages > 1 && (
                                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={myActionsPage === 0}
                                        onClick={() => loadMyActions(myActionsPage - 1)}
                                    >
                                        ←
                                    </Button>
                                    <span>Стр. {myActionsPage + 1} из {myActionsTotalPages}</span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={myActionsPage >= myActionsTotalPages - 1}
                                        onClick={() => loadMyActions(myActionsPage + 1)}
                                    >
                                        →
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMyActionsModal(false)}>Закрыть</Button>
                </Modal.Footer>
            </Modal>

            {/* Organizer sanction modal (ADMIN only) */}
            <Modal show={showOrganizerModal} onHide={() => setShowOrganizerModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Санкция против организатора</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Применяется, если организатор использует функционал платформы для травли участников.
                        Снимает с публикации все баттлы, аннулирует рейтинг.
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label>ID пользователя-организатора</Form.Label>
                        <Form.Control
                            type="text"
                            value={organizerUserId}
                            onChange={(e) => setOrganizerUserId(e.target.value)}
                            placeholder="UUID организатора"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Причина</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={organizerReason}
                            onChange={(e) => setOrganizerReason(e.target.value)}
                            placeholder="Использование бан-системы для травли участников лиги"
                        />
                    </Form.Group>
                    {organizerResult && (
                        <div className="admin-moderation__organizer-result">
                            <Badge bg="success">Санкция применена</Badge>
                            {organizerResult.cancelledBattleIds?.length > 0 && (
                                <p className="mt-2">
                                    Отменено баттлов: <strong>{organizerResult.cancelledBattleIds.length}</strong>
                                </p>
                            )}
                            {organizerResult.ratingReset && (
                                <p>Рейтинг аннулирован</p>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOrganizerModal(false)}>Закрыть</Button>
                    <Button variant="danger" onClick={handleSanctionOrganizer} disabled={actionLoading}>
                        {actionLoading ? 'Применение...' : 'Применить санкцию'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Detail modal */}
            <Modal show={showDetailModal} onHide={handleCloseDetailModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Детали модераторского действия</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailLoading ? (
                        <div className="text-center py-4">
                            <HrnbLoader />
                        </div>
                    ) : detailAction ? (
                        <div className="admin-moderation__detail">
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Карточка:</span>
                                <Badge bg={getCardBadgeVariant(detailAction.cardType)}>
                                    {detailAction.cardTypeDisplayName || MODERATION_CARD_TYPES[detailAction.cardType]?.label || detailAction.cardType}
                                </Badge>
                                {detailAction.durationDoubled && <Badge bg="danger" className="ms-1">x2 срок</Badge>}
                            </div>
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Статус:</span>
                                <Badge bg={getStatusBadgeVariant(detailAction.status)}>
                                    {MODERATION_ACTION_STATUSES[detailAction.status] || detailAction.status}
                                </Badge>
                                {detailAction.status === 'ACTIVE' && detailCanAppeal !== null && (
                                    <Badge bg={detailCanAppeal ? 'info' : 'secondary'} className="ms-1">
                                        {detailCanAppeal ? 'Можно апеллировать' : 'Апелляция недоступна'}
                                    </Badge>
                                )}
                            </div>
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Нарушитель:</span>
                                <span><strong>{detailAction.targetUsername}</strong> ({detailAction.targetUserId?.slice(0, 8)})</span>
                            </div>
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Модератор:</span>
                                <span><strong>{detailAction.moderatorUsername}</strong> ({detailAction.moderatorId?.slice(0, 8)})</span>
                            </div>
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Причина:</span>
                                <span>{detailAction.reason}</span>
                            </div>
                            {detailAction.platformRuleId && (
                                <div className="admin-moderation__detail-row">
                                    <span className="admin-moderation__detail-label">Правило платформы:</span>
                                    <span>{getPlatformRuleTitle(detailAction.platformRuleId)}</span>
                                </div>
                            )}
                            {detailAction.violations && (
                                <div className="admin-moderation__detail-row">
                                    <span className="admin-moderation__detail-label">Нарушения:</span>
                                    <span>{detailAction.violations}</span>
                                </div>
                            )}
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Длительность:</span>
                                <span>{detailAction.durationDays ? `${detailAction.durationDays} дн.` : '—'}</span>
                            </div>
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Истекает:</span>
                                <span>{formatDate(detailAction.expiresAt)}</span>
                            </div>
                            <div className="admin-moderation__detail-row">
                                <span className="admin-moderation__detail-label">Создано:</span>
                                <span>{formatDate(detailAction.createdAt)}</span>
                            </div>
                            {detailAction.ipAddress && (
                                <div className="admin-moderation__detail-row">
                                    <span className="admin-moderation__detail-label">IP-адрес:</span>
                                    <span className="admin-moderation__mono">
                                        {detailAction.ipAddress}
                                        {admin && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="ms-2 p-0"
                                                onClick={() => {
                                                    setShowDetailModal(false);
                                                    setShowIpModal(true);
                                                    setIpInput(detailAction.ipAddress);
                                                    setIpResult(null);
                                                }}
                                            >
                                                Проверить
                                            </Button>
                                        )}
                                    </span>
                                </div>
                            )}
                            {detailAction.hardwareId && (
                                <div className="admin-moderation__detail-row">
                                    <span className="admin-moderation__detail-label">Hardware ID:</span>
                                    <span className="admin-moderation__mono">
                                        {detailAction.hardwareId}
                                        {admin && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="ms-2 p-0"
                                                onClick={() => {
                                                    setShowDetailModal(false);
                                                    setShowHwidModal(true);
                                                    setHwidInput(detailAction.hardwareId);
                                                    setHwidResult(null);
                                                }}
                                            >
                                                Проверить
                                            </Button>
                                        )}
                                    </span>
                                </div>
                            )}
                            {detailAction.revokedAt && (
                                <>
                                    <hr />
                                    <div className="admin-moderation__detail-row">
                                        <span className="admin-moderation__detail-label">Отозвано:</span>
                                        <span>{formatDate(detailAction.revokedAt)}</span>
                                    </div>
                                    {detailAction.revokedBy && (
                                        <div className="admin-moderation__detail-row">
                                            <span className="admin-moderation__detail-label">Отозвал:</span>
                                            <span>{detailAction.revokedBy}</span>
                                        </div>
                                    )}
                                    {detailAction.revokeReason && (
                                        <div className="admin-moderation__detail-row">
                                            <span className="admin-moderation__detail-label">Причина отзыва:</span>
                                            <span>{detailAction.revokeReason}</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <p className="text-muted">Не удалось загрузить данные</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {detailAction?.status === 'ACTIVE' && (
                        <>
                            <Button
                                variant="outline-info"
                                onClick={() => { handleCloseDetailModal(); handleOpenRevokeModal(detailAction); }}
                            >
                                Отозвать
                            </Button>
                            {admin && (detailAction.cardType === 'YELLOW_CARD' || detailAction.cardType === 'RED_CARD') && !detailAction.durationDoubled && (
                                <Button
                                    variant="outline-danger"
                                    onClick={() => { handleCloseDetailModal(); handleDoubleDuration(detailAction); }}
                                >
                                    x2 срок
                                </Button>
                            )}
                        </>
                    )}
                    <Button variant="secondary" onClick={handleCloseDetailModal}>Закрыть</Button>
                </Modal.Footer>
            </Modal>

            {/* User history modal */}
            <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        История модерации: {historyUser?.username}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyLoading && (
                        <div className="text-center py-4">
                            <HrnbLoader />
                        </div>
                    )}

                    {historyError && (
                        <HrnbAlert type="danger">{historyError}</HrnbAlert>
                    )}

                    {!historyLoading && !historyError && historyActions.length === 0 && (
                        <p className="text-center text-muted py-3">
                            У пользователя нет модераторских действий
                        </p>
                    )}

                    {!historyLoading && !historyError && historyActions.length > 0 && (
                        <Table responsive hover className="admin-moderation__history-table">
                            <thead>
                                <tr>
                                    <th>Карточка</th>
                                    <th>Модератор</th>
                                    <th>Причина</th>
                                    <th>Статус</th>
                                    <th>Срок</th>
                                    <th>Дата</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyActions.map((hAction) => (
                                    <tr key={hAction.id} className={getRowColorClass(hAction.cardType)}>
                                        <td>
                                            <Badge bg={getCardBadgeVariant(hAction.cardType)}>
                                                {hAction.cardTypeDisplayName || MODERATION_CARD_TYPES[hAction.cardType]?.label || hAction.cardType}
                                            </Badge>
                                            {hAction.durationDoubled && (
                                                <Badge bg="danger" className="ms-1">x2</Badge>
                                            )}
                                        </td>
                                        <td>{hAction.moderatorUsername}</td>
                                        <td>
                                            <div className="admin-moderation__reason">
                                                {hAction.reason?.substring(0, 80)}
                                                {hAction.reason?.length > 80 ? '...' : ''}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg={getStatusBadgeVariant(hAction.status)}>
                                                {MODERATION_ACTION_STATUSES[hAction.status] || hAction.status}
                                            </Badge>
                                        </td>
                                        <td>
                                            {hAction.durationDays ? `${hAction.durationDays} дн.` : '—'}
                                        </td>
                                        <td>{formatDate(hAction.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseHistoryModal}>Закрыть</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminModeration;
